import { lazy, Suspense } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { Spinner } from '@shared/ui'
import { PendingRiskAgreementProvider } from '@app/providers/PendingRiskAgreementProvider'
import { MobileLayout } from '@widgets/mobile-layout'
import { AuthGuard, GuestGuard, MobileAccountRequiredGuard } from './guards'

const w = (Page: React.ComponentType) => (
  <Suspense fallback={<Spinner size="lg" fullscreen />}>
    <Page />
  </Suspense>
)

const HomePage = lazy(() => import('@pages/mobile/home'))
const NotFoundPage = lazy(() => import('@pages/not-found'))
const WsTestPage = lazy(() => import('@pages/ws-test'))

const MobileLoginPage = lazy(() => import('@pages/mobile/login'))
const MobileSignupPage = lazy(() => import('@pages/mobile/signup'))
const MobileTermsPage = lazy(() => import('@pages/mobile/terms'))
const MobileForgotPasswordPage = lazy(() => import('@pages/mobile/forgot-password'))
const MobileAccountPage = lazy(() => import('@pages/mobile/mypage/account'))
const MobileDeleteUserPage = lazy(() => import('@pages/mobile/mypage/delete-user'))
const MobilePasswordPage = lazy(() => import('@pages/mobile/mypage/password'))

const MobileMarketPage = lazy(() => import('@pages/mobile/market'))
const MobileMyPage = lazy(() => import('@pages/mobile/mypage'))
const MobilePnlPage = lazy(() => import('@pages/mobile/mypage/pnl'))
const MobileManagementPage = lazy(() => import('@pages/mobile/management'))
const MobileNotificationsPage = lazy(() => import('@pages/mobile/notifications'))
const MobileBotPage = lazy(() => import('@pages/mobile/bot'))

export const mobileRouter = createBrowserRouter([
  // 개발 전용
  { path: '/ws-test', element: w(WsTestPage) },

  // 비로그인 전용 (로그인 상태면 홈으로)
  {
    element: <GuestGuard />,
    children: [
      { path: '/login', element: w(MobileLoginPage) },
      { path: '/signup', element: w(MobileSignupPage) },
      { path: '/forgot-password', element: w(MobileForgotPasswordPage) },
    ],
  },

  // 누구나 접근 가능 (로그인 여부에 따라 내부 UI 분기)
  {
    element: (
      <PendingRiskAgreementProvider>
        <MobileLayout />
      </PendingRiskAgreementProvider>
    ),
    children: [
      { path: '/', element: w(HomePage) },
      { path: '/terms', element: w(MobileTermsPage) },
      { path: '/mypage', element: w(MobileMyPage) },
    ],
  },

  // 로그인 필수
  {
    element: (
      <PendingRiskAgreementProvider>
        <AuthGuard />
      </PendingRiskAgreementProvider>
    ),
    children: [
      { path: '/mypage/account', element: w(MobileAccountPage) },
      { path: '/mypage/delete-user', element: w(MobileDeleteUserPage) },
    ],
  },

  // 로그인 + svrId 필수 → 나머지 모든 페이지
  {
    element: (
      <PendingRiskAgreementProvider>
        <MobileAccountRequiredGuard />
      </PendingRiskAgreementProvider>
    ),
    children: [
      {
        element: <MobileLayout />,
        children: [
          { path: '/market', element: w(MobileMarketPage) },
          { path: '/mypage/pnl', element: w(MobilePnlPage) },
          { path: '/mypage/password', element: w(MobilePasswordPage) },
          { path: '/management', element: w(MobileManagementPage) },
          { path: '/notifications', element: w(MobileNotificationsPage) },
          { path: '/bot', element: w(MobileBotPage) },
        ],
      },
    ],
  },

  { path: '*', element: w(NotFoundPage) },
])
